import { importSleepRecords, type SleepImportRow } from '@/lib/health/sleepImport';
import { importHealthMetricRecords } from '@/lib/health/metricImport';
import { importHealthWorkoutRecords } from '@/lib/health/workoutImport';
import { createRecentHealthImportClient } from '@/lib/health/recentImport';
import { useState, useEffect, useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { CapacitorCalendar, CalendarPermissionScope } from "@ebarooni/capacitor-calendar";
import { runAppleCalendarSync, AppleCalendarStaleSyncError } from "@/lib/appleCalendarSync";
import { Health } from "@capgo/capacitor-health";
import { notifyNativeHealthChanged } from "@/lib/health/nativeHealthChanges";
import { claimRecentHealthMetadata, releaseRecentHealthMetadata } from "@/lib/health/syncCoordination";
import { runHealthHistorySync, readHealthSamplesBounded, readHealthWorkoutsBounded } from "@/lib/health/historySync";
import { parseHistoricalSyncResponse } from "@/lib/connectionHistoryResult";
import { supabase } from "@/integrations/supabase/client";
import { publishAgendaRefresh } from "@/lib/agendaChanges";
import { platformOpen } from "@/lib/platformOpen";
import { capacitorErrorMessage } from "@/lib/capacitorErrors";
import {
  HEALTH_READ_TYPES,
  readPermissionVerdict,
  repairPath,
  typesToSkip,
  type HealthReadType,
  type PermissionVerdict,
} from "@/lib/health/permissionState";
import { Sentry } from "@/lib/sentry";
import {
  buildSleepRows,
  buildWorkoutRows,
  buildMetricRows,
  aggregateSamplesByDay,
  filterTombstoned,
  healthWindowStart,
  HEALTH_DAYS_BACK,
  type HealthProvider,
  type DeletionTombstone,
} from "@/lib/health/healthMap";
import { deviceTimeZone } from "@/lib/reminderSchedule";
import {
  classifySync,
  shouldAdvanceWatermark,
  type TypeOutcome,
} from "@/lib/health/syncOutcome";

// Multi-provider facade scope. Two connection shapes coexist:
//   Type A — OAuth backend (init→callback→pull edge fns): strava, google_*,
//            outlook, notion, spotify
//   Type B — Native permission (Capacitor plugin reads on-device, writes via
//            RLS): apple_health (iOS HealthKit), health_connect (Android),
//            apple_calendar (iOS EventKit)
//
// oura e fitbit SAÍRAM (29/07). Não é limpeza cosmética: as seis edge
// functions (oura-oauth-init/callback/sync, fitbit-*) eram DEPLOY ÓRFÃO —
// tinham sido tiradas da UI havia tempo e, medido em produção, nunca tiveram
// UMA conexão na história inteira do produto. O código aqui continuava
// compilando e dando a impressão de que eram integrações vivas. O hub do SO
// (Apple Health / Health Connect) já lê Oura, Fitbit, WHOOP e Garmin de uma
// vez, então integração direta com cada wearable virou trabalho redundante.
export type Provider =
  | "dropbox" | "todoist" | "trello" | "slack" | "microsoft_teams"
  | "strava"          // Type A — Strava OAuth
  | "apple_health"    // Type B — iOS HealthKit via @capgo/capacitor-health
  | "health_connect"  // Type B — Android Health Connect via @capgo/capacitor-health
  | "google_calendar" // Type A — Google OAuth
  | "gmail"           // Type A — Google OAuth (Gmail modify + send, no mailbox copy)
  | "google_drive"    // Type A — Google OAuth (Drive read + app-file write)
  | "outlook"         // Type A — Microsoft Graph OAuth (Calendars.Read → agenda_events)
  | "outlook_mail"    // Type A — Microsoft Graph OAuth (Mail.ReadWrite + Mail.Send)
  | "onedrive"        // Type A — Microsoft Graph OAuth (Files.ReadWrite)
  | "google_tasks"    // Type A — Google OAuth (same client as Calendar, tasks.readonly → tasks table)
  | "apple_calendar"  // Type B — iOS EventKit via @ebarooni/capacitor-calendar (on-device → agenda_events)
  | "notion"          // Type A — Notion OAuth (pages → notes)
  | "google_contacts"
  | "microsoft_contacts"
  | "microsoft_todo"
  | "apple_contacts"
  | "apple_reminders"
  | "spotify";        // Type A — Spotify OAuth (listening → health_metrics)

// Map the active platform to the wearable provider it can serve. Returns
// null when running on web (or any non-iOS/Android target) so callers
// know to throw a translated "native-only" error.
export function platformHealthProvider(): "apple_health" | "health_connect" | null {
  const p = Capacitor.getPlatform();
  if (p === "ios") return "apple_health";
  if (p === "android") return "health_connect";
  return null;
}

export interface UserConnection {
  id: string;
  provider: string;
  status: "pending" | "active" | "expired" | "revoked" | "error";
  provider_user_id: string | null;
  expires_at: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  provider_metadata: Record<string, unknown> | null;
  // Estes dois existem para o card poder distinguir "OAuth em andamento" de
  // "tentativa morta". Antes o tipo não os declarava, o card não olhava para
  // eles, e uma linha pending renderizava sem NENHUM botão — 41 usuários
  // presos (64% de todas as tentativas da história) sem desfazer nem tentar
  // de novo. O select("*") sempre trouxe as colunas; faltava o tipo admitir.
  oauth_state_expires_at: string | null;
  created_at: string;
}

interface SyncResult {
  synced: number;
  total_fetched: number;
  errors: string[];
  status?: "ok" | "partial";
  deleted?: number;
  preserved_conflicts?: number;
  window?: { from: string; to: string; complete: boolean };
  history?: unknown;
}

export interface HistorySyncOptions {
  mode: "history";
  history_from?: string;
}

const STORAGE_KEY = "vyv_strava_oauth_state";

export function useConnections(opts?: { counts?: boolean }) {
  // counts: false = leitura enxuta — só a lista de user_connections, sem os
  // contadores decorativos (~7 queries head:true por mount). Para quem monta
  // este hook fora da tela de Conexões (o card de wearable em Sleep/Fitness)
  // os badges nem são renderizados; pagar 7 counts por página era desperdício.
  // Default é contar: a tela de Conexões não muda em nada.
  const countsEnabled = opts?.counts !== false;
  const [connections, setConnections] = useState<UserConnection[]>([]);
  /**
   * Não-nulo quando a LEITURA de user_connections falhou. Distinto de
   * `connections.length === 0`, que significa "não há conexão". Confundir os
   * dois fazia integração conectada se apresentar como desconectada sempre que
   * a sessão expirava (bug de 01/08 — ver o ramo de erro em refresh).
   */
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [stravaActivityCount, setStravaActivityCount] = useState<number | null>(null);
  const [googleTasksCount, setGoogleTasksCount] = useState<number | null>(null);
  const [healthItemCount, setHealthItemCount] = useState<number | null>(null);
  const [notionItemCount, setNotionItemCount] = useState<number | null>(null);
  const [spotifyItemCount, setSpotifyItemCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Por PROVIDER, nao um boolean global: com o boolean, clicar em qualquer
  // "Conectar" esmaecia os 8 cards ao mesmo tempo (auditoria 30/07).
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  const owner = useRef<string | null>(null);
  const authGeneration = useRef(0);
  const syncRequests = useRef(new Map<string, symbol>());
  const recentHealthControllers = useRef(new Map<symbol, AbortController>());
  const readVersion = useRef(0);
  const mounted = useRef(false);
  const readController = useRef<AbortController | null>(null);
  const readFailed = useRef(false);
  const refresh = useCallback(async () => {
    const run = ++readVersion.current;
    readController.current?.abort();
    const controller = new AbortController(); readController.current = controller;
    const current = () => mounted.current && run === readVersion.current;
    const timedOut = () => {
      controller.abort();
      if (current()) { readVersion.current++; readFailed.current = true; setConnectionsError("Connections unavailable"); setLoading(false); }
    };
    // Auth may legitimately wait for the shared 30s session lock. Its budget
    // must not be confused with the shorter PostgREST/network budget.
    let timer = window.setTimeout(timedOut, 35000);
    setLoading(true);
    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (!current()) return;
      if (authError || !session) throw authError ?? new Error("Not authenticated");
      window.clearTimeout(timer);
      timer = window.setTimeout(timedOut, 12000);
      const userId = session.user.id;
      if (owner.current !== userId) { owner.current = userId; setConnections([]); }
      const { data, error } = await supabase.from("user_connections")
        .select("id,provider,status,provider_user_id,expires_at,last_sync_at,last_sync_status,last_sync_error,provider_metadata,created_at")
        .eq("user_id", userId).order("created_at", { ascending: false }).abortSignal(controller.signal);
      if (!current()) return;
      if (error) throw error;
      setConnections((data ?? []).map(connection => {
        const metadata = connection.provider_metadata;
        const expires = metadata && typeof metadata === "object" && !Array.isArray(metadata)
          ? metadata.oauth_state_expires_at : null;
        return { ...connection, oauth_state_expires_at: typeof expires === "string" ? expires : null };
      }) as unknown as UserConnection[]);
      readFailed.current = false;
      setConnectionsError(null);
      setLoading(false);
      window.clearTimeout(timer);
      if (!countsEnabled) return;
      const count = async (table: "workout_sessions" | "sleep_logs" | "health_metrics" | "notes" | "tasks", source: string): Promise<number | null> => {
        try {
          const result = await supabase.from(table).select("id", { count: "exact", head: true }).eq("user_id", userId).eq("source", source).abortSignal(controller.signal);
          if (result.error) { console.warn("[useConnections] count unavailable", table); return null; }
          return result.count;
        } catch {
          console.warn("[useConnections] count unavailable", table);
          return null;
        }
      };
      const healthProvider = platformHealthProvider();
      // Decorative counts may be unavailable without invalidating the list.
      timer = window.setTimeout(() => controller.abort(), 5000);
      const values = await Promise.all([
        count("workout_sessions", "strava"), count("notes", "notion"), count("health_metrics", "spotify"), count("tasks", "google_tasks"),
        healthProvider ? Promise.all([count("sleep_logs", healthProvider), count("workout_sessions", healthProvider), count("health_metrics", healthProvider)]).then(parts => parts.some(n => n === null) ? null : parts.reduce<number>((sum, n) => sum + (n ?? 0), 0)) : null,
      ]);
      if (!current()) return;
      setStravaActivityCount(values[0]); setNotionItemCount(values[1]); setSpotifyItemCount(values[2]); setGoogleTasksCount(values[3]); setHealthItemCount(values[4]);
    } catch (cause) {
      if (current()) { readFailed.current = true; console.error("[useConnections] read failed", cause); setConnectionsError(capacitorErrorMessage(cause, "Connections unavailable")); }
    } finally { window.clearTimeout(timer); if (current()) setLoading(false); }
  }, [countsEnabled]);

  useEffect(() => {
    mounted.current = true;
    const healthControllers = recentHealthControllers.current;
    const cancelPendingRead = () => { readVersion.current++; readController.current?.abort(); };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (next === owner.current && !readFailed.current) return;
      authGeneration.current++;
      for (const controller of healthControllers.values()) controller.abort();
      healthControllers.clear();
      syncRequests.current.clear(); setSyncing(false);
      readVersion.current++; readController.current?.abort(); owner.current = next;
      setConnections([]); setStravaActivityCount(null); setNotionItemCount(null); setSpotifyItemCount(null); setGoogleTasksCount(null); setHealthItemCount(null);
      setConnectionsError(next ? null : "Not authenticated"); setLoading(!!next);
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    const recover = () => { if (readFailed.current) void refresh(); };
    window.addEventListener("online", recover);
    void refresh();
    return () => {
      mounted.current = false; cancelPendingRead();
      for (const controller of healthControllers.values()) controller.abort();
      healthControllers.clear();
      subscription.unsubscribe(); window.removeEventListener("online", recover);
    };
  }, [refresh]);

  const connectStrava = useCallback(async () => {
    setConnectingProvider("strava");
    try {
      const { data, error } = await supabase.functions.invoke("strava-oauth-init", {
        body: {},
      });

      if (error || !data?.authorizeUrl || !data?.state) {
        console.error("[useConnections] init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from strava-oauth-init");
      }

      sessionStorage.setItem(STORAGE_KEY, data.state);
      platformOpen(data.authorizeUrl);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const syncStrava = useCallback(async (options?: HistorySyncOptions): Promise<SyncResult> => {
    if (syncRequests.current.has("strava")) throw new Error("sync_in_progress");
    const token = Symbol("strava"); syncRequests.current.set("strava", token);
    const generation = authGeneration.current;
    let userId: string | null = null;
    const current = () => mounted.current && generation === authGeneration.current && (!owner.current || owner.current === userId);
    setSyncing(true);
    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session?.access_token) throw authError ?? new Error("Not authenticated");
      userId = session.user.id;
      if (!current()) throw new Error("stale_sync");
      const { data, error } = await supabase.functions.invoke("strava-sync", {
        body: options ? { mode: "history", ...(options.history_from !== undefined ? { history_from: options.history_from } : {}) } : {},
        headers: { Authorization: "Bearer " + session.access_token },
      });
      if (!current()) throw new Error("stale_sync");
      if (error) {
        console.error("[useConnections] sync error:", error);
        throw error;
      }

      const result = options ? parseHistoricalSyncResponse(data) : data as SyncResult;
      await refresh();
      if (!current()) throw new Error("stale_sync");
      return result;
    } finally {
      if (syncRequests.current.get("strava") === token) syncRequests.current.delete("strava");
      if (current()) setSyncing(false);
    }
  }, [refresh]);

  const disconnectStrava = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "strava");
    if (!conn) return;

    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");

    if (error) {
      console.error("[useConnections] disconnect error:", error);
      throw error;
    }

    await refresh();
  }, [connections, refresh]);

  const stravaConnection = connections.find((c) => c.provider === "strava") || null;

  // ── Google Calendar (Type A: OAuth) handlers ──
  // Mirrors the Strava trio. connectGoogleCal calls google-oauth-init which
  // stores the CSRF nonce server-side in user_connections.provider_metadata
  // (not sessionStorage like Strava — Google's redirect URI is server-to-
  // server, so the callback edge fn validates state via DB lookup).
  const connectGoogleCal = useCallback(async () => {
    setConnectingProvider("google_calendar");
    try {
      const { data, error } = await supabase.functions.invoke("google-oauth-init", {
        body: {},
      });

      if (error || !data?.authorize_url) {
        console.error("[useConnections] google init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from google-oauth-init");
      }

      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const syncGoogleCal = useCallback(async (): Promise<SyncResult> => {
    const userId = owner.current, epoch = authGeneration.current;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-cal-pull", {
        body: {},
      });

      if (error) {
        console.error("[useConnections] google sync error:", error);
        throw error;
      }

      if (userId && mounted.current && owner.current === userId && authGeneration.current === epoch) publishAgendaRefresh(userId);
      await refresh();
      // google-cal-pull returns { synced, updated, skipped, deleted, errors }.
      // Coerce to the shared SyncResult shape used by the Strava facade so
      // callers can render a unified "N activities/events imported" string.
      const payload = data as { synced?: number; updated?: number; skipped?: number; deleted?: number; errors?: string[] } | null;
      return {
        synced: ((payload?.synced ?? 0) + (payload?.updated ?? 0)),
        total_fetched: ((payload?.synced ?? 0) + (payload?.updated ?? 0) + (payload?.skipped ?? 0)),
        errors: payload?.errors ?? [],
      };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const disconnectGoogleCal = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "google_calendar");
    if (!conn) return;

    // V1: delete the user_connections row (matches Strava). Token revocation
    // at Google's side is a nice-to-have we can layer in V2 via
    // POST oauth2.googleapis.com/revoke; for now the row removal + Google's
    // own UI revoke flow are sufficient. Server still holds ciphertext until
    // the DELETE runs.
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] google disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  const googleCalConnection = connections.find((c) => c.provider === "google_calendar") || null;

  // ── Outlook Calendar (Type A: OAuth) handlers ──
  // Same shape as Google Calendar (server-side CSRF state in
  // user_connections.provider_metadata), but via Microsoft Graph and a
  // SEPARATE OAuth app (OUTLOOK_OAUTH_CLIENT_ID). Syncs into agenda_events
  // (source='outlook') — the same table Google Calendar feeds.
  const connectOutlook = useCallback(async () => {
    setConnectingProvider("outlook");
    try {
      const { data, error } = await supabase.functions.invoke("outlook-cal-oauth-init", {
        body: {},
      });
      if (error || !data?.authorize_url) {
        console.error("[useConnections] outlook init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from outlook-cal-oauth-init");
      }
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  // ── Google Tasks (Type A: OAuth) handlers ──
  // Same shape as Google Calendar (server-side CSRF state in
  // user_connections.provider_metadata, reuses the same Google OAuth client),
  // but requests the tasks.readonly scope and syncs into the `tasks` table
  // (source='google_tasks') instead of agenda_events.
  const connectGoogleTasks = useCallback(async () => {
    setConnectingProvider("google_tasks");
    try {
      const { data, error } = await supabase.functions.invoke("google-tasks-oauth-init", {
        body: {},
      });
      if (error || !data?.authorize_url) {
        console.error("[useConnections] google tasks init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from google-tasks-oauth-init");
      }
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const syncOutlook = useCallback(async (): Promise<SyncResult> => {
    const userId = owner.current, epoch = authGeneration.current;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("outlook-cal-pull", {
        body: {},
      });
      if (error) {
        console.error("[useConnections] outlook sync error:", error);
        throw error;
      }
      if (userId && mounted.current && owner.current === userId && authGeneration.current === epoch) publishAgendaRefresh(userId);
      await refresh();
      // outlook-cal-pull returns { synced, updated, skipped, deleted, errors }.
      // Coerce to the shared SyncResult shape.
      const payload = data as { synced?: number; updated?: number; skipped?: number; deleted?: number; errors?: string[] } | null;
      return {
        synced: ((payload?.synced ?? 0) + (payload?.updated ?? 0)),
        total_fetched: ((payload?.synced ?? 0) + (payload?.updated ?? 0) + (payload?.skipped ?? 0)),
        errors: payload?.errors ?? [],
      };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const googleTasksSyncRun = useRef(0);
  const syncGoogleTasks = useCallback(async (): Promise<SyncResult> => {
    const run = ++googleTasksSyncRun.current;
    const accountVersion = readVersion.current, userId = owner.current;
    const current = () => mounted.current && run === googleTasksSyncRun.current && accountVersion === readVersion.current && owner.current === userId;
    const check = () => { if (!current()) throw new Error("Connection session changed"); };
    setSyncing(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      check();
      if (sessionError || !session?.access_token || !userId || session.user.id !== userId) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("google-tasks-pull", {
        body: {},
        headers: { Authorization: "Bearer " + session.access_token },
      });
      check();
      if (error) {
        console.error("[useConnections] google tasks sync error:", error);
        throw error;
      }
      const payload = data as { synced: number; updated: number; deleted?: number; skipped: number; errors: string[]; partial?: boolean; coverage?: { complete: boolean; tasks: number } } | null;
      const legacy = payload && !["deleted", "partial", "coverage"].some(key => Object.prototype.hasOwnProperty.call(payload, key));
      const countValid = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
      if (!payload || ![payload.synced, payload.updated, payload.skipped].every(countValid) ||
        !Array.isArray(payload.errors) || payload.errors.some(value => typeof value !== "string") ||
        (!legacy && (!countValid(payload.deleted) || !countValid(payload.coverage?.tasks) || typeof payload.partial !== "boolean" || typeof payload.coverage?.complete !== "boolean"))) {
        throw new Error("Google Tasks sync response invalid");
      }
      const refreshVersion = readVersion.current + 1;
      await refresh();
      if (!mounted.current || run !== googleTasksSyncRun.current || owner.current !== userId || readVersion.current !== refreshVersion) throw new Error("Connection session changed");
      return {
        synced: payload.synced + payload.updated,
        total_fetched: legacy ? payload.synced + payload.updated + payload.skipped : payload.coverage!.tasks,
        deleted: payload.deleted ?? 0,
        // Frontend can ship before Edge. Old responses prove neither coverage
        // nor reconciliation; refresh their counts but never claim full success.
        status: legacy || payload.partial || !payload.coverage?.complete || payload.errors.length ? "partial" : "ok",
        errors: legacy ? [...payload.errors, "coverage_unverified"] : payload.errors,
      };
    } finally {
      if (mounted.current && run === googleTasksSyncRun.current) setSyncing(false);
    }
  }, [refresh]);

  const disconnectOutlook = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "outlook");
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] outlook disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  const disconnectGoogleTasks = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "google_tasks");
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] google tasks disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  const outlookConnection = connections.find((c) => c.provider === "outlook") || null;
  const gmailConnection = connections.find((c) => c.provider === "gmail") || null;
  const outlookMailConnection = connections.find((c) => c.provider === "outlook_mail") || null;
  const googleDriveConnection = connections.find((c) => c.provider === "google_drive") || null;
  const oneDriveConnection = connections.find((c) => c.provider === "onedrive") || null;
  const googleTasksConnection = connections.find((c) => c.provider === "google_tasks") || null;

  // ── Email Executor connections (Type A: OAuth, live mailbox access) ──
  // Email is intentionally NOT synchronized into VYV tables. The executor reads
  // provider data on demand and write actions are idempotent/audited server-side.
  const connectGmail = useCallback(async () => {
    setConnectingProvider("gmail");
    try {
      const { data, error } = await supabase.functions.invoke("gmail-oauth-init", { body: {} });
      if (error || !data?.authorize_url) throw error || new Error("Invalid response from gmail-oauth-init");
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const connectOutlookMail = useCallback(async () => {
    setConnectingProvider("outlook_mail");
    try {
      const { data, error } = await supabase.functions.invoke("outlook-mail-oauth-init", { body: {} });
      if (error || !data?.authorize_url) throw error || new Error("Invalid response from outlook-mail-oauth-init");
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const probeEmail = useCallback(async (provider: "gmail" | "outlook_mail"): Promise<SyncResult> => {
    setSyncing(true);
    try {
      const { error } = await supabase.functions.invoke("email-executor", { body: { provider, action: "probe" } });
      if (error) throw error;
      await refresh();
      return { synced: 1, total_fetched: 1, errors: [] };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const syncGmail = useCallback(() => probeEmail("gmail"), [probeEmail]);
  const syncOutlookMail = useCallback(() => probeEmail("outlook_mail"), [probeEmail]);

  const disconnectEmail = useCallback(async (provider: "gmail" | "outlook_mail") => {
    const conn = connections.find((c) => c.provider === provider);
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) throw error;
    await refresh();
  }, [connections, refresh]);

  const disconnectGmail = useCallback(() => disconnectEmail("gmail"), [disconnectEmail]);
  const disconnectOutlookMail = useCallback(() => disconnectEmail("outlook_mail"), [disconnectEmail]);

  // ── Files & Knowledge Executor connections ───────────────────────────────
  const connectGoogleDrive = useCallback(async () => {
    setConnectingProvider("google_drive");
    try {
      const { data, error } = await supabase.functions.invoke("google-drive-oauth-init", { body: {} });
      if (error || !data?.authorize_url) throw error || new Error("Invalid response from google-drive-oauth-init");
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const connectOneDrive = useCallback(async () => {
    setConnectingProvider("onedrive");
    try {
      const { data, error } = await supabase.functions.invoke("onedrive-oauth-init", { body: {} });
      if (error || !data?.authorize_url) throw error || new Error("Invalid response from onedrive-oauth-init");
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const probeFiles = useCallback(async (provider: "google_drive" | "onedrive"): Promise<SyncResult> => {
    setSyncing(true);
    try {
      const { error } = await supabase.functions.invoke("files-executor", { body: { provider, action: "probe" } });
      if (error) throw error;
      await refresh();
      return { synced: 1, total_fetched: 1, errors: [] };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const syncGoogleDrive = useCallback(() => probeFiles("google_drive"), [probeFiles]);
  const syncOneDrive = useCallback(() => probeFiles("onedrive"), [probeFiles]);

  const disconnectFiles = useCallback(async (provider: "google_drive" | "onedrive") => {
    const conn = connections.find((c) => c.provider === provider);
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) throw error;
    await refresh();
  }, [connections, refresh]);

  const disconnectGoogleDrive = useCallback(() => disconnectFiles("google_drive"), [disconnectFiles]);
  const disconnectOneDrive = useCallback(() => disconnectFiles("onedrive"), [disconnectFiles]);

  // ── Health (Type B: Capacitor plugin) handlers ──
  //
  // Uma API para os dois sistemas: @capgo/capacitor-health expõe a MESMA
  // superfície para HealthKit (iOS) e Health Connect (Android) e roteia para a
  // ponte nativa certa por baixo. Por isso não há ramo por plataforma aqui —
  // só o `provider` muda, e ele é derivado da plataforma ativa.
  //
  // ⚠️ HISTÓRIA QUE NÃO PODE SE REPETIR: o plugin já esteve no build duas
  // vezes e saiu duas vezes. A segunda (commit 5466a4f, build 27, 08/06) foi
  // uma REPROVAÇÃO da Apple, motivo G2.5.1: o HealthKit.framework estava
  // linkado no binário sem NENHUMA funcionalidade visível — connectHealth era
  // um stub que lançava e os cards estavam escondidos. A regra que sai disso:
  // o plugin e as telas que MOSTRAM o dado têm que viajar no mesmo build.
  // É por isso que este PR traz junto a tela de métricas com editar, apagar e
  // histórico — não é escopo extra, é a condição de aprovação.
  // Ponte para o sync, que é declarado ABAIXO de connectHealth. Um ref em vez
  // de referência direta por dois motivos: evita pôr syncHealth no array de
  // deps de connectHealth (o que recriaria o callback a cada refresh e ainda
  // criaria um ciclo, já que syncHealth depende de refresh), e mantém a leitura
  // sempre na versão corrente. Atribuído logo após a definição de syncHealth.
  const syncHealthRef = useRef<((p: HealthProvider) => Promise<SyncResult>) | null>(null);

  const connectHealth = useCallback(async (
    provider: HealthProvider,
  ): Promise<void> => {
    const platformProvider = platformHealthProvider();
    if (platformProvider === null) {
      throw new Error("health is only available on iOS or Android");
    }
    if (platformProvider !== provider) {
      throw new Error(provider + " is not the provider for this platform (" + platformProvider + ")");
    }

    setConnectingProvider(provider);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const availability = await Health.isAvailable();
      if (!availability.available) {
        // No Android isso normalmente quer dizer "Health Connect não está
        // instalado". A mensagem precisa chegar ao usuário — silenciar aqui
        // deixaria um botão que não faz nada.
        throw new Error("health store unavailable: " + (availability.reason || "unknown"));
      }

      // requestHistoryAccess pede a permissão extra do Health Connect que
      // libera ler além dos ~30 dias. É ignorada no iOS (HealthKit não tem
      // equivalente nem teto de 30 dias) e é silenciosamente pulada em
      // provider Android antigo — em nenhum dos dois casos ela faz a chamada
      // falhar, então pedir sempre é seguro.
      const status = await Health.requestAuthorization({
        read: [...HEALTH_READ_TYPES],
        requestHistoryAccess: true,
      });

      // VERDADE POR TIPO. Era `readAuthorized.length > 0` — um tipo de cinco
      // carimbava 'active', e quem concedeu só Sono via "conectado ✓" sem
      // receber métrica nenhuma (issue #480). O veredito separa os três
      // estados; o do meio continua 'active' porque sono e treino ESTÃO
      // entrando, mas carrega `permission_partial` e a lista do que falta.
      const verdict = readPermissionVerdict(status);
      const granted = verdict.kind !== "none";

      // §4.9: .select() encadeado e checagem de linha — uma rejeição de RLS
      // não pode se passar por "conectado".
      const { data: upserted, error: upsertErr } = await supabase
        .from("user_connections")
        .upsert(
          {
            user_id: user.id,
            provider: provider,
            status: verdict.status,
            last_sync_at: null,
            // Antes gravava `null` no caminho feliz — e `null` é exatamente o
            // que faz o card NÃO mostrar faixa (ConnectionCard: syncProblem
            // exige !== null). Permissão parcial nascia invisível até o
            // primeiro sync. Agora o estado do meio já nasce dito.
            last_sync_status: verdict.lastSyncStatus,
            last_sync_error:
              verdict.kind === "full" ? null : "readDenied: " + verdict.denied.join(","),
            provider_metadata: {
              platform: Capacitor.getPlatform(),
              read_authorized: verdict.authorized,
              read_denied: verdict.denied,
              history_access: status.historyAccessAuthorized ?? null,
            },
          } as never,
          { onConflict: "user_id,provider" },
        )
        .select();
      if (upsertErr) throw upsertErr;
      if (!upserted || upserted.length === 0) {
        throw new Error("user_connections upsert returned no row (RLS or constraint silently dropped the write)");
      }

      await refresh();
      if (!granted) {
        throw new Error("health permissions not granted");
      }

      // ── A CAUSA RAIZ (auditoria 03/08) ────────────────────────────────
      // Até aqui connectHealth terminava. Autorizar e LER são duas funções, e
      // a primeira nunca encadeava na segunda: o app pedia permissão, o SO
      // concedia, e o app parava esperando um segundo toque.
      //
      // O estado de produção provava isso de três formas independentes:
      // 1 linha apple_health active desde 31/07 com last_sync_at NULL; o
      // carimbo de :821 é INCONDICIONAL e vem depois de blocos que engolem os
      // próprios erros, então NULL só é possível se syncHealth nunca chegou lá;
      // e no Sentry, feature:health em 90 dias = 2 eventos, ambos step=connect,
      // ZERO step=sync. Três toques em "Conectar" em dois dias, nenhum em
      // "Sincronizar". health_metrics: zero linhas desde sempre.
      //
      // `void` + catch próprio de propósito: a leitura pode levar segundos e
      // pode falhar (HRV não existe em todo aparelho). Conectar JÁ deu certo —
      // a linha está gravada e a permissão concedida. Transformar "conectei"
      // em "falhou" por causa da leitura seria trocar um bug por outro, e o
      // erro do sync já é persistido em last_sync_status/last_sync_error por
      // syncHealth e mostrado no card.
      void syncHealthRef.current?.(provider).catch((e) => {
        console.error("[useConnections] first sync after connect failed:", e, { provider });
      });
    } catch (err) {
      // Log + Sentry ANTES de repropagar (§4.7). A ponte nativa é o ponto de
      // falha mais provável e ela REJEITA em vez de devolver estado negado.
      console.warn("[useConnections] connectHealth failed:", err);
      Sentry.captureException(err, { tags: { feature: "health", step: "connect", provider } });
      throw err;
    } finally {
      setConnectingProvider(null);
    }
  }, [refresh]);

  // As constantes e o cálculo da janela vivem em `_lib/health/healthMap.ts`:
  // são lógica pura e METADE do fix de 04/08 (a que conserta o valor), e
  // dentro do hook não havia como testá-las sem device.

  /**
   * Lê as marcas d'água por tipo de `user_connections.provider_metadata`.
   *
   * Moram no jsonb que já existe, e não em coluna nova, porque o conjunto de
   * tipos lidos muda (hoje 5, amanhã peso/temperatura/SpO2) e congelar isso em
   * coluna garante uma migration por tipo novo. Formato:
   *   provider_metadata.watermarks = { sleep: ISO, workouts: ISO, steps: ISO, … }
   *
   * Nunca lança: metadata corrompido devolve {} e o sync cai na janela padrão,
   * que é o comportamento seguro (lê mais, não menos).
   */
  const readHealthWatermarks = (
    conns: UserConnection[],
    provider: HealthProvider,
  ): Record<string, string> => {
    try {
      const conn = conns.find((c) => c.provider === provider);
      const meta = (conn as { provider_metadata?: unknown } | undefined)?.provider_metadata;
      if (!meta || typeof meta !== "object") return {};
      const marks = (meta as { watermarks?: unknown }).watermarks;
      if (!marks || typeof marks !== "object") return {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(marks as Record<string, unknown>)) {
        if (typeof v === "string" && Number.isFinite(Date.parse(v))) out[k] = v;
      }
      return out;
    } catch (e) {
      console.error("[useConnections] watermark read failed — caindo na janela padrão:", e, { provider });
      return {};
    }
  };

  const syncHealth = useCallback(async (
    provider: HealthProvider,
    options?: HistorySyncOptions,
  ): Promise<SyncResult> => {
    const platformProvider = platformHealthProvider();
    if (platformProvider !== provider) {
      throw new Error(provider + " is not the provider for this platform");
    }

    if (syncRequests.current.has(provider)) throw new Error("sync_in_progress");
    const token = Symbol(provider); syncRequests.current.set(provider, token);
    const generation = authGeneration.current;
    let syncUserId: string | null = null;
    const current = () => mounted.current && generation === authGeneration.current && (!owner.current || owner.current === syncUserId);
    let recentController: AbortController | null = null;
    let releaseRecentClaim: (() => Promise<void>) | undefined;
    const assertCurrent = () => { if (!current() || recentController?.signal.aborted) throw new Error("stale_sync"); };
    setSyncing(true);
    const errors: string[] = [];
    let synced = 0;
    let totalFetched = 0;
    let preservedConflicts = 0;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const userId = user.id;
      syncUserId = userId;
      if (!current()) throw new Error("stale_sync");
      if (options) {
        const { data: connection, error } = await supabase.from("user_connections")
          .select("id,provider_metadata,status").eq("user_id", userId).eq("provider", provider).maybeSingle();
        if (!current()) throw new Error("stale_sync");
        if (error || !connection || connection.status !== "active") throw error ?? new Error("connection_inactive");
        const result = await runHealthHistorySync({
          Health, sb: supabase, userId, provider, connection,
          mode: options.history_from === undefined ? "history_continue" : "history",
          history_from: options.history_from, current,
        });
        if (!current()) throw new Error("stale_sync");
        notifyNativeHealthChanged(userId, {
          sleep: result.history.coverage_by_type.sleep.written,
          workouts: result.history.coverage_by_type.workouts.written,
          health: result.history.coverage_by_type.steps.written + result.history.coverage_by_type.heart_rate.written + result.history.coverage_by_type.hrv.written,
        });
        await refresh();
        if (!current()) throw new Error("stale_sync");
        return result;
      }

      // Pin every recent request to the authenticated owner and this run's bearer.
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      assertCurrent();
      if (sessionError || !session?.access_token || session.user.id !== userId) throw sessionError ?? new Error("Not authenticated");
      const bearer = session.access_token;
      const authorization = "Bearer " + bearer;
      recentController = new AbortController();
      recentHealthControllers.current.set(token, recentController);
      const signal = recentController.signal;

      // Read a fresh snapshot: a recent sync must preserve concurrent historical progress.
      const { data: recentConnection, error: recentConnectionError } = await supabase.from("user_connections")
        .select("id,provider_metadata,status").eq("user_id", userId).eq("provider", provider)
        .abortSignal(signal).setHeader("Authorization", authorization).maybeSingle();
      assertCurrent();
      if (recentConnectionError || !recentConnection || recentConnection.status !== "active") throw recentConnectionError ?? new Error("connection_inactive");
      const healthConnectionId = recentConnection.id;
      const recentRunId = crypto.randomUUID();
      const existingMeta = claimRecentHealthMetadata(recentConnection.provider_metadata, recentRunId);
      let claimQuery = supabase.from("user_connections").update({ provider_metadata: existingMeta } as never)
        .eq("user_id", userId).eq("provider", provider).eq("id", healthConnectionId).eq("status", "active");
      claimQuery = recentConnection.provider_metadata == null ? claimQuery.is("provider_metadata", null)
        : claimQuery.eq("provider_metadata", JSON.stringify(recentConnection.provider_metadata));
      releaseRecentClaim = async () => {
        if (!current() || signal.aborted) return;
        const fresh = await supabase.from("user_connections").select("id,provider_metadata")
          .eq("user_id", userId).eq("provider", provider).eq("id", healthConnectionId).eq("status", "active")
          .abortSignal(signal).setHeader("Authorization", authorization).maybeSingle();
        assertCurrent();
        if (fresh.error || !fresh.data) return;
        const metadata = fresh.data.provider_metadata as Record<string, unknown> | null;
        if ((metadata?.recent_sync as Record<string, unknown> | undefined)?.run_id !== recentRunId) return;
        await supabase.from("user_connections").update({ provider_metadata: releaseRecentHealthMetadata(metadata, recentRunId) } as never)
          .eq("user_id", userId).eq("provider", provider).eq("id", healthConnectionId).eq("status", "active")
          .eq("provider_metadata", JSON.stringify(metadata)).abortSignal(signal).setHeader("Authorization", authorization);
      };
      const claim = await claimQuery.select("id,provider_metadata").abortSignal(signal).setHeader("Authorization", authorization);
      assertCurrent();
      if (claim.error) throw claim.error;
      if (claim.data?.length !== 1 || claim.data[0].id !== healthConnectionId
        || (claim.data[0].provider_metadata as Record<string, unknown> | null)?.recent_sync == null
        || ((claim.data[0].provider_metadata as Record<string, unknown>).recent_sync as Record<string, unknown>).run_id !== recentRunId) throw new Error("sync_in_progress");
      // Capture the exact persisted metadata, including the server's expired-lease cleanup.
      recentConnection.provider_metadata = structuredClone(claim.data[0].provider_metadata);
      Object.assign(existingMeta, structuredClone(claim.data[0].provider_metadata));
      const recentImportClient = createRecentHealthImportClient({
        client: supabase, ownerId: userId, connectionId: healthConnectionId,
        provider, bearer, signal, assertCurrent, recentRunId,
      });

      // ── A PERMISSÃO É CONFERIDA A CADA SYNC ───────────────────────────
      // `requestAuthorization` só existia em connectHealth: a permissão era
      // decidida no dia da conexão e nunca mais reconsultada, então quem
      // concedeu parcialmente (ou revogou depois, no app do sistema) ficava
      // com o estado congelado para sempre — 16 conexões 'active' cheias de
      // SecurityException dentro (issue #480).
      //
      // `checkAuthorization` CONSULTA sem abrir folha nenhuma: é barato, não
      // interrompe, e faz o banco refletir o aparelho de hoje em vez do
      // aparelho do dia da conexão. Falha aqui NÃO derruba o sync — sem
      // veredito seguimos como antes, lendo tudo e deixando o erro de cada
      // tipo falar, que é o comportamento antigo.
      let verdict: PermissionVerdict | null = null;
      try {
        const check = await Health.checkAuthorization({ read: [...HEALTH_READ_TYPES] });
        assertCurrent();
        verdict = readPermissionVerdict(check);
      } catch (e) {
        assertCurrent();
        console.warn("[useConnections] checkAuthorization failed — seguindo sem veredito:", e);
        errors.push("permission_check: " + capacitorErrorMessage(e));
      }
      // No Android, tipo negado lança SecurityException a cada leitura e o
      // erro afoga o `last_sync_error` dos outros. Pular deixa os concedidos
      // sincronizarem limpos. No iOS a lista significa "nunca perguntado" e
      // NÃO se pula nada — ver o cabeçalho de permissionState.ts.
      const skip = new Set<HealthReadType>(
        verdict ? typesToSkip(verdict, Capacitor.getPlatform()) : [],
      );

      // ── CONTAGEM POR TIPO (20/08) ─────────────────────────────────────
      // Um TypeOutcome por tipo lido: o que o plugin devolveu (read), o que o
      // upsert CONFIRMOU (written, do .select() — §4.9), e se errou. É daqui
      // que saem o rótulo (classifySync), o avanço de marca d'água
      // (shouldAdvanceWatermark) e os contadores gravados em provider_metadata
      // — o que faltou em 20/08 para diagnosticar "sincronizou e não gravou"
      // sem log de aparelho.
      const outcomes: Record<TypeOutcome["type"], TypeOutcome> = {
        sleep: { type: "sleep", attempted: false, read: 0, written: 0, errored: false },
        workouts: { type: "workouts", attempted: false, read: 0, written: 0, errored: false },
        steps: { type: "steps", attempted: false, read: 0, written: 0, errored: false },
        heart_rate: { type: "heart_rate", attempted: false, read: 0, written: 0, errored: false },
        hrv: { type: "hrv", attempted: false, read: 0, written: 0, errored: false },
      };

      // ── JANELA DE LEITURA, a partir da marca d'água ───────────────────
      // POR JANELA DE DATAS, nunca "desde o último sucesso": se o usuário passa
      // duas semanas sem abrir, "desde o último sucesso" deixaria um buraco que
      // nunca mais fecha. Aqui a marca d'água só ENCOLHE a janela quando o sync
      // é recente; ela nunca a torna mais estreita que a sobreposição, e nunca
      // mais larga que o teto.
      //
      // Por que não anchor/change token: o plugin não os expõe. `readSamples` e
      // `queryAggregated` não têm anchor nem na entrada nem na saída, e o de
      // `queryWorkouts` é paginação intra-consulta (pageToken no Android,
      // cursor ISO no iOS) — não sobrevive entre syncs e não vê deleção.
      // Incremental de verdade exige código nativo; a marca por data entrega o
      // objetivo (não reler tudo) com o que existe hoje.
      const endDate = new Date();

      // ── FUSO DO USUÁRIO, não do aparelho ──────────────────────────────
      // `user_whisper_preferences.timezone` é onde o fuso do perfil já mora
      // (gravado no primeiro load autenticado por ensureWhisperTimezone e
      // nunca sobrescrito) e é o que o dispatch de lembretes usa. Reusar
      // aqui em vez de inventar fonte nova: o dia do agregado tem de ser o
      // MESMO dia que o resto do produto chama de dia.
      // Falha de leitura cai no aparelho, que é o comportamento antigo — e
      // loga, porque cair no aparelho em silêncio é como o bug de 04/08
      // passaria despercebido de novo.
      let userTz = deviceTimeZone();
      try {
        const { data: tzRow, error: tzErr } = await supabase
          .from("user_whisper_preferences").select("timezone").eq("user_id", userId)
          .abortSignal(signal).setHeader("Authorization", authorization).maybeSingle();
        assertCurrent();
        if (tzErr) {
          console.error("[useConnections] leitura de fuso falhou, usando o do aparelho:", tzErr);
        } else if (tzRow?.timezone) {
          userTz = tzRow.timezone;
        }
      } catch (e) {
        assertCurrent();
        console.error("[useConnections] leitura de fuso lançou, usando o do aparelho:", e);
      }

      const watermarks = readHealthWatermarks([{ provider, provider_metadata: existingMeta }] as UserConnection[], provider);
      const windowStartFor = (key: string): string =>
        healthWindowStart(watermarks[key], { now: endDate, tz: userTz });

      const startIso = healthWindowStart(null, { now: endDate, tz: userTz });
      const endIso = endDate.toISOString();
      // Marcas d'água desta rodada, por tipo. Só é gravada a de quem leu SEM
      // erro: avançar a marca de um tipo que falhou seria pular o buraco que
      // acabou de se abrir.
      const nextWatermarks: Record<string, string> = {};

      // As lápides do usuário. Uma leitura por sync, comparada em memória —
      // sem isto, apagar dado importado é teatro: some da tela e volta no
      // próximo sync (regra 30/06).
      // `integration_deletions` é nova e ainda não está no types.ts gerado —
      // mesmo cast que useExecutorAttachments/useNudges usam para as tabelas
      // criadas depois do último `supabase gen types`.
      // Paginado: o PostgREST corta TODA resposta em 1000 linhas, devolve
      // HTTP 200 e não sinaliza o corte — uma lápide além da milésima seria
      // invisível e a linha correspondente voltaria no sync. É o mesmo modo de
      // falha silenciosa que o _shared/pageRows.ts existe para matar, e a
      // ordem por `id` é o que torna o laço decidível (page incompleta = fim).
      const TOMB_PAGE = 500;
      const tombRows: DeletionTombstone[] = [];
      let tombErr: unknown = null;
      let tombComplete = false;
      for (let page = 0; page < 40; page++) {
        assertCurrent();
        const from = page * TOMB_PAGE;
        const { data, error } = await supabase
          .from("integration_deletions" as never)
          .select("table_name, source, external_id")
          .eq("user_id", userId)
          .eq("source", provider)
          .order("id", { ascending: true })
          .range(from, from + TOMB_PAGE - 1)
          .abortSignal(signal).setHeader("Authorization", authorization);
        assertCurrent();
        if (error) { tombErr = error; break; }
        const batch = (data || []) as DeletionTombstone[];
        for (const r of batch) tombRows.push(r);
        if (batch.length < TOMB_PAGE) { tombComplete = true; break; }
      }
      if (!tombComplete && !tombErr) tombErr = new Error("tombstone_page_limit");
      if (tombErr) {
        // Fail-CLOSED: sem saber o que o usuário apagou, gravar seria
        // ressuscitar. Melhor abortar e deixar o erro visível.
        console.error("[useConnections] health tombstone read failed — aborting sync:", tombErr);
        throw tombErr;
      }
      const tombstones = tombRows;

      // ── 1. Sono → sleep_logs ──────────────────────────────────────────
      if (skip.has("sleep")) {
        // Não é erro: é permissão que o usuário não deu. Entra na lista para
        // o card poder dizer o que falta, sem stack trace de SecurityException.
        errors.push("sleep: permission_denied");
      } else try {
        outcomes.sleep.attempted = true;
        // `readSamples` NÃO tem anchor — nem em QueryOptions nem em
        // ReadSamplesResult. Não existe paginação disponível para sono, e
        // portanto não dá para garantir leitura completa de uma janela densa.
        // O que NÃO é aceitável é perder em silêncio: se voltar exatamente o
        // teto, é quase certo que havia mais, e isso vira erro legível em
        // last_sync_error em vez de dado que some sem ninguém saber.
        const SLEEP_LIMIT = 2000;
        const sleepStart = windowStartFor("sleep");
        const sleepRes = await Health.readSamples({
          dataType: "sleep",
          startDate: sleepStart,
          endDate: endIso,
          limit: SLEEP_LIMIT,
        });
        assertCurrent();
        const sleepSamples = sleepRes.samples || [];
        outcomes.sleep.read = sleepSamples.length;
        if (sleepSamples.length >= SLEEP_LIMIT) {
          console.error("[useConnections] sleep read hit the plugin limit — janela provavelmente truncada", {
            provider, limit: SLEEP_LIMIT, days: HEALTH_DAYS_BACK,
          });
          errors.push(
            "sono: leitura atingiu o teto de " + SLEEP_LIMIT + " amostras em " +
            HEALTH_DAYS_BACK + " dias — pode haver noites não importadas nesta janela",
          );
          // Truncagem provável = leitura suspeita. Marca como errado para o
          // rótulo continuar 'partial' (era o que o critério por erros dava) e
          // para a marca d'água NÃO avançar por cima de uma janela incompleta —
          // no dia em que a marca deixar de ser inerte, avançar aqui viraria
          // buraco permanente exatamente nas janelas mais densas.
          outcomes.sleep.errored = true;
        }
        totalFetched += sleepSamples.length;
        const sleepRows = filterTombstoned(
          buildSleepRows(sleepSamples, userId, provider),
          tombstones,
          "sleep_logs",
        );

        for (let offset = 0; offset < sleepRows.length; offset += 100) {
          assertCurrent();
          const imported = await importSleepRecords(recentImportClient,
            sleepRows.slice(offset, offset + 100) as SleepImportRow[], userId, assertCurrent);
          outcomes.sleep.written += imported.written;
          synced += imported.written;
        }
        // Marca só avança em tipo que leu SEM erro E leu ALGUMA COISA (20/08).
        // Avançar a de um tipo que falhou seria pular exatamente o buraco que
        // acabou de se abrir — e avançar sobre leitura VAZIA foi o que as
        // quatro conexões de 20/08 fizeram cinco vezes cada.
        if (shouldAdvanceWatermark(outcomes.sleep)) nextWatermarks["sleep"] = endIso;
      } catch (e) {
        assertCurrent();
        console.error("[useConnections] health sleep sync failed:", e);
        outcomes.sleep.errored = true;
        errors.push("sleep: " + capacitorErrorMessage(e));
      }

      // ── 2. Treinos → workout_sessions ─────────────────────────────────
      if (skip.has("workouts")) {
        errors.push("workouts: permission_denied");
      } else try {
        outcomes.workouts.attempted = true;
        // PAGINAÇÃO REAL. Até 03/08 isto era uma chamada só com limit: 200 e
        // TUDO além do ducentésimo treino da janela era descartado em
        // silêncio — sem erro, sem log, sem contagem. O código nunca tinha
        // rodado, então o defeito só apareceria no PRIMEIRO sync de um
        // usuário com histórico longo, que é o pior momento possível.
        //
        // O `anchor` do plugin é EXATAMENTE isto e nada além: a doc dele
        // (definitions.d.ts:124-129) diz "Anchor for pagination… On iOS, this
        // is the ISO 8601 cursor… On Android, this uses Health Connect's
        // pageToken". Não é HKQueryAnchor nem getChangesToken — não detecta
        // deleção nem edição de amostra antiga, e por isso NÃO serve como
        // marca d'água entre syncs. Serve para terminar de ler ESTA janela.
        const workoutStart = windowStartFor("workouts");
        const { workouts: allWorkouts } = await readHealthWorkoutsBounded(Health, workoutStart, endIso, provider, assertCurrent);
        assertCurrent();
        outcomes.workouts.read = allWorkouts.length;
        totalFetched += allWorkouts.length;
        const workoutRows = filterTombstoned(
          buildWorkoutRows(allWorkouts, userId, provider),
          tombstones,
          "workout_sessions",
        );
        // The transaction rechecks edits and deletions on the current row.
        // Count only a whole validated batch; retain earlier confirmations if
        // a later request fails, without advancing this type's watermark.
        const confirmedWorkoutIds = new Set<string>();
        for (let offset = 0; offset < workoutRows.length; offset += 100) {
          await importHealthWorkoutRecords({
            client: supabase, rows: workoutRows.slice(offset, offset + 100),
            ownerId: userId, connectionId: healthConnectionId, provider,
            bearer, signal, assertCurrent, recentRunId, confirmedIds: confirmedWorkoutIds,
            onConfirmed: delta => {
              outcomes.workouts.written += delta.written;
              synced += delta.written;
              preservedConflicts += delta.preserved_conflicts;
            },
          });
        }
        if (shouldAdvanceWatermark(outcomes.workouts)) nextWatermarks["workouts"] = endIso;
      } catch (e) {
        assertCurrent();
        console.error("[useConnections] health workout sync failed:", e);
        outcomes.workouts.errored = true;
        errors.push("workouts: " + capacitorErrorMessage(e));
      }

      // ── 3. FC / HRV / passos → health_metrics ─────────────────────────
      // queryAggregated em vez de readSamples: a frequência cardíaca gera
      // milhares de amostras por dia e nós só mostramos o número do dia.
      // Agregar na ponte nativa evita trazer tudo para o JS para depois jogar
      // fora.
      // `aggregate: false` = o Health Connect NÃO agrega esse tipo. Está no
      // código do plugin: HealthManager.kt:903 lança
      // "Aggregated queries are not supported for {tipo}. Use readSamples
      // instead." para heartRateVariability, respiratoryRate,
      // oxygenSaturation e vo2Max. Pedir agregação de HRV no Android falharia
      // em TODO sync — a conexão ficaria eternamente 'partial' e HRV nunca
      // entraria. Para esses tipos lemos os samples e agregamos por dia no
      // JS (aggregateSamplesByDay), o que também deixa iOS e Android com o
      // mesmo comportamento em vez de um ramo por plataforma.
      const METRIC_QUERIES: {
        dataType: "steps" | "heartRate" | "heartRateVariability";
        metricType: "steps" | "heart_rate" | "hrv";
        aggregation: "sum" | "average";
        unit: string;
        aggregate: boolean;
      }[] = [
        { dataType: "steps", metricType: "steps", aggregation: "sum", unit: "count", aggregate: true },
        { dataType: "heartRate", metricType: "heart_rate", aggregation: "average", unit: "bpm", aggregate: true },
        { dataType: "heartRateVariability", metricType: "hrv", aggregation: "average", unit: "millisecond", aggregate: false },
      ];

      for (const q of METRIC_QUERIES) {
        assertCurrent();
        if (skip.has(q.dataType)) {
          errors.push(q.metricType + ": permission_denied");
          continue;
        }
        try {
          outcomes[q.metricType].attempted = true;
          const metricStart = windowStartFor(q.metricType);
          let buckets;
          if (q.aggregate) {
            const aggRes = await Health.queryAggregated({
              dataType: q.dataType,
              startDate: metricStart,
              endDate: endIso,
              bucket: "day",
              aggregation: q.aggregation,
            });
            assertCurrent();
            buckets = aggRes.samples || [];
          } else {
            const { samples } = await readHealthSamplesBounded(Health, "heartRateVariability", metricStart, endIso,
              assertCurrent);
            buckets = aggregateSamplesByDay(samples, q.aggregation, userTz);
          }
          assertCurrent();
          outcomes[q.metricType].read = buckets.length;
          totalFetched += buckets.length;
          const metricRows = filterTombstoned(
            buildMetricRows(buckets, userId, provider, q.metricType, q.unit, userTz),
            tombstones,
            "health_metrics",
          );
          // A duplicate origin must fail before any chunk is written, as the
          // former single bulk statement did. Chunk boundaries cannot permit
          // the same provider origin to overwrite an earlier acknowledgement.
          if (new Set(metricRows.map(row => row.external_id)).size !== metricRows.length) {
            throw new Error("health_metric_origins_duplicated");
          }
          // Respeita a CORREÇÃO do usuário. Editar é requisito desta tela, e
          // sem esta leitura a edição duraria até o próximo sync — que
          // reescreveria a mesma chave natural sem avisar ninguém.
          let editedKeys = new Set<string>();
          if (metricRows.length > 0) {
            assertCurrent();
            const { data: editedRows, error: editedErr } = await supabase
              .from("health_metrics")
              .select("metric_type, recorded_at, metadata")
              .eq("user_id", userId)
              .eq("source", provider)
              .eq("metric_type", q.metricType)
              .gte("recorded_at", startIso)
              .not("metadata", "is", null)
              .abortSignal(signal).setHeader("Authorization", authorization);
            assertCurrent();
            if (editedErr) {
              // Fail-closed: sem saber o que foi corrigido, escrever
              // sobrescreveria correção. Melhor pular este tipo nesta rodada.
              console.error("[useConnections] edited-rows read failed:", editedErr);
              throw editedErr;
            }
            editedKeys = new Set(
              ((editedRows as { metric_type: string; recorded_at: string; metadata: Record<string, unknown> | null }[]) || [])
                .filter((r) => r.metadata && r.metadata.edited_at)
                .map((r) => r.metric_type + "|" + r.recorded_at),
            );
          }
          const writable = metricRows.filter(
            (r) => !editedKeys.has(r.metric_type + "|" + r.recorded_at),
          );

          // The trigger is the final authority, including guards created after
          // the precheck. Recover only its known rolled-back protected rejection.
          for (let offset = 0; offset < writable.length; offset += 100) {
            await importHealthMetricRecords({
              client: recentImportClient, rows: writable.slice(offset, offset + 100),
              ownerId: userId, bearer, signal, assertCurrent,
              onConfirmed: delta => {
                outcomes[q.metricType].written += delta.written;
                synced += delta.written;
                preservedConflicts += delta.preserved_conflicts;
              },
            });
          }
          if (shouldAdvanceWatermark(outcomes[q.metricType])) {
            nextWatermarks[q.metricType] = endIso;
          }
        } catch (e) {
          assertCurrent();
          // Uma métrica indisponível (HRV não existe em todo device) não pode
          // derrubar as outras duas.
          console.warn("[useConnections] health metric " + q.metricType + " failed:", e);
          outcomes[q.metricType].errored = true;
          errors.push(q.metricType + ": " + capacitorErrorMessage(e));
        }
      }

      // §4.9: `.select()` encadeado + checagem de linha. Este carimbo é o ÚNICO
      // sinal que distingue "nunca tentou" de "tentou e falhou" — foi ele, NULL
      // em produção, que provou na auditoria de 03/08 que o sync jamais rodou.
      // Sem o .select(), um UPDATE barrado por RLS (ou que casasse zero linhas
      // depois de o usuário desconectar no meio do sync) seria indistinguível
      // de sucesso, e o sinal que serve de prova voltaria a mentir.
      const stampedAt = new Date().toISOString();
      // As marcas d'água entram no MESMO update do carimbo, e por merge sobre
      // as antigas: um tipo que falhou nesta rodada mantém a marca anterior em
      // vez de ser zerado. `provider_metadata` é reescrito inteiro (jsonb não
      // tem merge parcial pelo PostgREST), então o que já estava lá — platform,
      // read_authorized, history_access, gravados por connectHealth — é
      // preservado explicitamente. Perder esses campos apagaria o registro de
      // QUAIS tipos o usuário autorizou.
      // A permissão MANDA no rótulo: um sync sem erro nenhum ainda é
      // 'permission_partial' se faltam tipos, senão a pessoa veria "tudo certo"
      // enquanto passos e FC seguem fora. COM veredito, quem decide é
      // classifySync sobre as CONTAGENS — é aqui que "cinco tipos vazios com
      // permissão cheia" deixa de carimbar 'ok' e vira 'empty' (20/08). SEM
      // veredito (checkAuthorization falhou), cai no critério antigo por erros,
      // deliberadamente fora do classifySync: sem saber a permissão, "empty"
      // seria chute, e o erro de permission_check já garante 'partial' como
      // sempre garantiu.
      const outcomeList = Object.values(outcomes);
      const syncLabel: string = verdict
        ? classifySync(outcomeList, verdict.lastSyncStatus)
        : errors.length === 0
          ? "ok"
          : "partial";
      // Os contadores que faltaram em 20/08: com eles, "sincronizou e não
      // gravou" se responde com um SELECT em vez de log de aparelho.
      // `attempted` entra no shape (21/08): sem ele, tipo pulado por permissão
      // grava {read:0, written:0, errored:false} — idêntico a "tentou e veio
      // vazio" — e distinguir os dois voltaria a exigir inferência cruzando
      // read_denied, que é a classe de raciocínio que errou o diagnóstico de
      // 20/08.
      const lastSyncCounts = Object.fromEntries(
        outcomeList.map((o) => [
          o.type,
          { attempted: o.attempted, read: o.read, written: o.written, errored: o.errored },
        ]),
      );
      const mergedMeta = {
        ...releaseRecentHealthMetadata(existingMeta, recentRunId),
        watermarks: { ...watermarks, ...nextWatermarks },
        last_sync_outcome: syncLabel,
        last_sync_counts: lastSyncCounts,
        last_sync_preserved_conflicts: preservedConflicts,
        last_sync_window_start: startIso,
        // O que o APARELHO diz HOJE, não o que dizia no dia da conexão. É
        // daqui que o card tira os tipos que faltam, por nome.
        ...(verdict
          ? {
              read_authorized: verdict.authorized,
              read_denied: verdict.denied,
              permission_checked_at: stampedAt,
            }
          : {}),
      };
      // Vazio com permissão CHEIA é evento: todas as portas abertas e nada
      // atravessou — ou o aparelho realmente não tem dado, ou o plugin está
      // devolvendo vazio em silêncio, e só o volume agregado no Sentry separa
      // os dois. Por construção 'empty' só nasce com veredito full
      // (lastSyncStatus null), então não dispara em permissão parcial — ali o
      // vazio é esperado e viraria ruído. captureMessage, não capture-
      // Exception: não há exceção, e fabricar uma poluiria a agregação por
      // stack.
      //
      // SÓ NA TRANSIÇÃO (21/08): sem OTA e com o painel de saúde virando item
      // de primeiro nível, cada usuário SEM wearable seria um warning POR DIA
      // — a dedupe do Sentry é por sessão (janela de 60s) e não cruza dias.
      // O sinal forte é "veio de ok e virou empty" (fluía e parou); "sempre
      // foi empty" é usuário sem relógio. `previousOutcome` vai no contexto
      // para separar os dois na leitura do evento.
      //
      // existingMeta comes from the fresh connection snapshot read above.
      const previousOutcome =
        typeof existingMeta.last_sync_outcome === "string"
          ? existingMeta.last_sync_outcome
          : null;
      if (syncLabel === "empty" && previousOutcome !== "empty") {
        Sentry.captureMessage("health sync empty with full permissions", {
          level: "warning",
          tags: { feature: "health", step: "sync_empty", provider },
          contexts: {
            health: {
              counts: lastSyncCounts,
              previousOutcome,
              platform: Capacitor.getPlatform(),
              windowStart: startIso,
              tz: userTz,
            },
          },
        });
      }
      assertCurrent();
      notifyNativeHealthChanged(userId, {
        sleep: outcomes.sleep.written, workouts: outcomes.workouts.written,
        health: outcomes.steps.written + outcomes.heart_rate.written + outcomes.hrv.written,
      });
      let stampQuery = supabase
        .from("user_connections")
        .update({
          last_sync_at: stampedAt,
          last_sync_status: syncLabel,
          last_sync_error: errors.length === 0 ? null : errors.slice(0, 5).join("; ").slice(0, 500),
          provider_metadata: mergedMeta,
          // `status` só muda quando NADA é concedido — aí é 'error' de verdade
          // e o card manda reconectar. Parcial continua 'active' porque o que
          // foi concedido está entrando (e a coluna tem CHECK: não existe
          // 'partial', e migration está fora desta correção).
          ...(verdict && verdict.kind === "none" ? { status: "error" } : {}),
        } as never)
        .eq("user_id", userId)
        .eq("provider", provider)
        .eq("id", healthConnectionId)
        .eq("status", "active");
      stampQuery = recentConnection.provider_metadata == null ? stampQuery.is("provider_metadata", null)
        : stampQuery.eq("provider_metadata", JSON.stringify(recentConnection.provider_metadata));
      const { data: stamped, error: stampErr } = await stampQuery.select("id")
        .abortSignal(signal).setHeader("Authorization", authorization);
      assertCurrent();
      if (stampErr) {
        console.error("[useConnections] health last_sync stamp failed:", stampErr, { provider });
        errors.push("metadata_commit_failed");
      } else if (!stamped || stamped.length === 0) {
        console.error("[useConnections] health last_sync stamp matched zero rows", { provider, userId: userId.slice(0, 8) });
        errors.push("connection_changed");
      }

      if (!current()) throw new Error("stale_sync");
      await refresh();
      if (!current()) throw new Error("stale_sync");
      return { synced, total_fetched: totalFetched, preserved_conflicts: preservedConflicts, errors, status: errors.length > 0 ? "partial" : "ok" };
    } catch (err) {
      // `warn` e não `error`: a captura explícita logo abaixo É o evento (ela
      // leva tags.feature; um evento de console não levaria), e a linha de
      // console vira breadcrumb anexado a ela. Com `error` o par viraria DOIS
      // eventos não correlacionados, que a dedupe por mensagem não junta
      // (§11a). Mesma escolha de connectAppleCal/syncAppleCal neste arquivo.
      console.warn("[useConnections] syncHealth failed:", err);
      Sentry.captureException(err, { tags: { feature: "health", step: "sync", provider } });
      throw err;
    } finally {
      try { await releaseRecentClaim?.(); } catch (error) { console.warn("[useConnections] recent health lease release failed", capacitorErrorMessage(error)); }
      recentHealthControllers.current.delete(token);
      recentController?.abort();
      if (syncRequests.current.get(provider) === token) syncRequests.current.delete(provider);
      if (current()) setSyncing(false);
    }
  }, [refresh]);

  // Liga o ref que connectHealth usa para disparar o primeiro sync. Fica FORA
  // de useEffect de propósito: o efeito só rodaria após o commit, e connectHealth
  // pode ser invocado no mesmo tick em que o hook monta.
  syncHealthRef.current = syncHealth;

  /**
   * O reparo da permissão parcial — o único caminho que conserta de verdade.
   *
   * Android: abre a tela do Health Connect. Pedir de novo não serve, o SO
   * limita re-prompt e a folha pode simplesmente não aparecer.
   * iOS: pede de novo. A folha só surge para os tipos NUNCA perguntados (que é
   * o que `readDenied` significa lá), e é no-op para o resto; depois
   * sincroniza para o veredito se atualizar na hora.
   */
  const fixHealthPermissions = useCallback(async (
    provider: HealthProvider,
  ): Promise<void> => {
    const caminho = repairPath(Capacitor.getPlatform());
    if (caminho === "open_health_connect") {
      await Health.openHealthConnectSettings();
      return;
    }
    if (caminho === "request_again") {
      await Health.requestAuthorization({ read: [...HEALTH_READ_TYPES] });
      await syncHealthRef.current?.(provider);
    }
  }, []);

  const disconnectHealth = useCallback(async (
    provider: HealthProvider,
  ): Promise<void> => {
    const conn = connections.find((c) => c.provider === provider);
    if (!conn) return;

    // Mirror Strava's behavior: delete the row entirely. iOS/Android Health
    // permissions themselves persist at the OS layer — VYV can only forget
    // the link, not revoke the OS grant (user has to do that in Settings).
    const { data: removed, error } = await supabase
      .from("user_connections")
      .delete()
      .eq("id", conn.id)
      .eq("user_id", owner.current!)
      .select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] health disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  // ── Notion (Type A: OAuth) handlers ──
  // Mesma forma do Google Tasks (state CSRF server-side em
  // user_connections.provider_metadata). Importa páginas do Notion para a
  // tabela `notes` com source='notion'.
  const connectNotion = useCallback(async () => {
    setConnectingProvider("notion");
    try {
      const { data, error } = await supabase.functions.invoke("notion-oauth-init", { body: {} });
      if (error || !data?.authorize_url) {
        console.error("[useConnections] notion init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from notion-oauth-init");
      }
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const syncNotion = useCallback(async (): Promise<SyncResult> => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("notion-pull", { body: {} });
      if (error) {
        console.error("[useConnections] notion sync error:", error);
        throw error;
      }
      await refresh();
      const payload = data as { synced?: number; updated?: number; skipped?: number; errors?: string[] } | null;
      return {
        synced: ((payload?.synced ?? 0) + (payload?.updated ?? 0)),
        total_fetched: ((payload?.synced ?? 0) + (payload?.updated ?? 0) + (payload?.skipped ?? 0)),
        errors: payload?.errors ?? [],
      };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const disconnectNotion = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "notion");
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] notion disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  const notionConnection = connections.find((c) => c.provider === "notion") || null;

  // ── Spotify (Type A: OAuth) handlers ──
  // Minutos de escuta por dia viram linha em health_metrics
  // (metric_type='listening_minutes') — insumo das leituras de sono e foco,
  // que é exatamente para o que esta integração foi escopada.
  const connectSpotify = useCallback(async () => {
    setConnectingProvider("spotify");
    try {
      const { data, error } = await supabase.functions.invoke("spotify-oauth-init", { body: {} });
      if (error || !data?.authorize_url) {
        console.error("[useConnections] spotify init error:", error, data);
        setConnectingProvider(null);
        throw error || new Error("Invalid response from spotify-oauth-init");
      }
      platformOpen(data.authorize_url);
    } catch (err) {
      setConnectingProvider(null);
      throw err;
    }
  }, []);

  const syncSpotify = useCallback(async (): Promise<SyncResult> => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("spotify-pull", { body: {} });
      if (error) {
        console.error("[useConnections] spotify sync error:", error);
        throw error;
      }
      await refresh();
      const payload = data as { synced?: number; updated?: number; skipped?: number; errors?: string[] } | null;
      return {
        synced: ((payload?.synced ?? 0) + (payload?.updated ?? 0)),
        total_fetched: ((payload?.synced ?? 0) + (payload?.updated ?? 0) + (payload?.skipped ?? 0)),
        errors: payload?.errors ?? [],
      };
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const disconnectSpotify = useCallback(async () => {
    const conn = connections.find((c) => c.provider === "spotify");
    if (!conn) return;
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) {
      console.error("[useConnections] spotify disconnect error:", error);
      throw error;
    }
    await refresh();
  }, [connections, refresh]);

  const spotifyConnection = connections.find((c) => c.provider === "spotify") || null;

  const healthConnection = connections.find(
    (c) => c.provider === "apple_health" || c.provider === "health_connect",
  ) || null;

  // ── Apple Calendar (Type B: EventKit, on-device) handlers ──
  // Native reads cover only their exact window. Reconciliation is owner
  // scoped, paginated and conservative about local edits and tombstones.
  const appleCalSyncRun = useRef(0);
  const appleCalConnectRun = useRef(0);

  const connectAppleCal = useCallback(async (): Promise<void> => {
    if (Capacitor.getPlatform() !== "ios") {
      throw new Error("apple_calendar is only available on iOS");
    }
    const run = ++appleCalConnectRun.current;
    const accountVersion = readVersion.current;
    let userId: string | null = owner.current;
    const current = () => mounted.current && run === appleCalConnectRun.current && accountVersion === readVersion.current && owner.current === userId;
    const check = () => { if (!current()) throw new AppleCalendarStaleSyncError(); };
    setConnectingProvider("apple_calendar");
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw authError ?? new Error("Not authenticated");
      userId = user.id; check();
      // iOS 17+ needs full access to read EventKit. Never write a connection
      // belonging to an account that changed while the native prompt was open.
      const { result } = await CapacitorCalendar.requestFullCalendarAccess(); check();
      const granted = result === "granted";
      const { data: upserted, error: upsertErr } = await supabase
        .from("user_connections")
        .upsert({
          user_id: userId, provider: "apple_calendar", status: granted ? "active" : "error",
          last_sync_at: null, last_sync_status: granted ? null : "permission_denied",
          last_sync_error: granted ? null : "access: " + result,
          provider_metadata: { platform: "ios", access: result },
        } as never, { onConflict: "user_id,provider" }).select("id");
      check();
      if (upsertErr) throw upsertErr;
      if (upserted?.length !== 1) throw new Error("Apple Calendar connection write not confirmed");
      const refreshVersion = readVersion.current + 1;
      await refresh();
      if (!mounted.current || run !== appleCalConnectRun.current || owner.current !== userId || readVersion.current !== refreshVersion) throw new AppleCalendarStaleSyncError();
      if (!granted) throw new Error("Calendar access not granted (state: " + result + ")");
    } catch (err) {
      if (!(err instanceof AppleCalendarStaleSyncError)) {
        console.warn("[useConnections] connectAppleCal failed:", err);
        Sentry.captureException(err, { tags: { feature: "apple_calendar", step: "connect" } });
      }
      throw err;
    } finally {
      if (mounted.current && run === appleCalConnectRun.current) {
        setConnectingProvider(value => value === "apple_calendar" ? null : value);
      }
    }
  }, [refresh]);

  const syncAppleCal = useCallback(async (): Promise<SyncResult> => {
    if (Capacitor.getPlatform() !== "ios") {
      throw new Error("apple_calendar is only available on iOS");
    }
    const run = ++appleCalSyncRun.current;
    const accountVersion = readVersion.current;
    let userId: string | null = owner.current;
    const current = () => mounted.current && run === appleCalSyncRun.current && accountVersion === readVersion.current && owner.current === userId;
    const check = () => { if (!current()) throw new AppleCalendarStaleSyncError(); };
    setSyncing(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw authError ?? new Error("Not authenticated");
      userId = user.id; check();
      const result = await runAppleCalendarSync({
        client: supabase, userId,
        device: {
          readPermission: () => CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.READ_CALENDAR }),
          listEventsInRange: range => CapacitorCalendar.listEventsInRange(range),
        },
        isCurrent: current,
      });
      check();
      // Partial reads/writes leave the last fully successful watermark alone.
      // Status itself is a checked write: RLS returning 0 is not publication.
      const statusPatch = {
        ...(result.status === "ok" ? { last_sync_at: new Date().toISOString() } : {}),
        last_sync_status: result.status,
        last_sync_error: result.errors.length ? result.errors.slice(0, 5).join("; ").slice(0, 500) : null,
      };
      const { data: saved, error: statusError } = await supabase.from("user_connections")
        .update(statusPatch as never).eq("user_id", userId).eq("provider", "apple_calendar").select("id");
      check();
      if (statusError || saved?.length !== 1) {
        result.status = "partial";
        result.errors.push("sync_status_write: " + capacitorErrorMessage(statusError, "write not confirmed"));
      }
      publishAgendaRefresh(userId);
      const refreshVersion = readVersion.current + 1;
      await refresh();
      if (!mounted.current || run !== appleCalSyncRun.current || owner.current !== userId || readVersion.current !== refreshVersion) throw new AppleCalendarStaleSyncError();
      return result;
    } catch (err) {
      if (!(err instanceof AppleCalendarStaleSyncError)) {
        console.warn("[useConnections] syncAppleCal failed:", err);
        Sentry.captureException(err, { tags: { feature: "apple_calendar", step: "sync" } });
      }
      throw err;
    } finally {
      if (mounted.current && run === appleCalSyncRun.current) setSyncing(false);
    }
  }, [refresh]);

  const disconnectAppleCal = useCallback(async (): Promise<void> => {
    const conn = connections.find((c) => c.provider === "apple_calendar");
    if (!conn) return;
    // V1: forget the link only. The OS-level EventKit grant persists until the
    // user revokes it in iOS Settings (same as the health providers). Imported
    // agenda_events rows are left as-is on disconnect.
    const { data: removed, error } = await supabase.from("user_connections").delete().eq("id", conn.id).eq("user_id", owner.current!).select("id");
    if (!error && removed?.length !== 1) throw new Error("Connection removal not confirmed");
    if (error) throw error;
    await refresh();
  }, [connections, refresh]);

  const appleCalConnection = connections.find((c) => c.provider === "apple_calendar") || null;

  // ── Multi-provider facade ──
  // Dispatches to per-Type handlers. Existing per-provider callers (Strava)
  // keep working unchanged — facade is purely additive.
  const connect = useCallback(async (provider: Provider): Promise<void> => {
    switch (provider) {
      case "strava":
        return connectStrava();
      case "google_calendar":
        return connectGoogleCal();
      case "gmail":
        return connectGmail();
      case "google_drive":
        return connectGoogleDrive();
      case "outlook":
        return connectOutlook();
      case "outlook_mail":
        return connectOutlookMail();
      case "onedrive":
        return connectOneDrive();
      case "google_tasks":
        return connectGoogleTasks();
      case "apple_health":
      case "health_connect":
        return connectHealth(provider);
      case "apple_calendar":
        return connectAppleCal();
      case "notion":
        return connectNotion();
      case "spotify":
        return connectSpotify();
    }
  }, [connectStrava, connectGoogleCal, connectGmail, connectGoogleDrive, connectOutlook, connectOutlookMail, connectOneDrive, connectGoogleTasks, connectHealth, connectAppleCal, connectNotion, connectSpotify]);

  const sync = useCallback(async (provider: Provider): Promise<SyncResult> => {
    switch (provider) {
      case "strava":
        return syncStrava();
      case "google_calendar":
        return syncGoogleCal();
      case "gmail":
        return syncGmail();
      case "google_drive":
        return syncGoogleDrive();
      case "outlook":
        return syncOutlook();
      case "outlook_mail":
        return syncOutlookMail();
      case "onedrive":
        return syncOneDrive();
      case "google_tasks":
        return syncGoogleTasks();
      case "apple_health":
      case "health_connect":
        return syncHealth(provider);
      case "apple_calendar":
        return syncAppleCal();
      case "notion":
        return syncNotion();
      case "spotify":
        return syncSpotify();
    }
  }, [syncStrava, syncGoogleCal, syncGmail, syncGoogleDrive, syncOutlook, syncOutlookMail, syncOneDrive, syncGoogleTasks, syncHealth, syncAppleCal, syncNotion, syncSpotify]);

  const disconnect = useCallback(async (provider: Provider): Promise<void> => {
    switch (provider) {
      case "strava":
        return disconnectStrava();
      case "google_calendar":
        return disconnectGoogleCal();
      case "gmail":
        return disconnectGmail();
      case "google_drive":
        return disconnectGoogleDrive();
      case "outlook":
        return disconnectOutlook();
      case "outlook_mail":
        return disconnectOutlookMail();
      case "onedrive":
        return disconnectOneDrive();
      case "google_tasks":
        return disconnectGoogleTasks();
      case "apple_health":
      case "health_connect":
        return disconnectHealth(provider);
      case "apple_calendar":
        return disconnectAppleCal();
      case "notion":
        return disconnectNotion();
      case "spotify":
        return disconnectSpotify();
    }
  }, [disconnectStrava, disconnectGoogleCal, disconnectGmail, disconnectGoogleDrive, disconnectOutlook, disconnectOutlookMail, disconnectOneDrive, disconnectGoogleTasks, disconnectHealth, disconnectAppleCal, disconnectNotion, disconnectSpotify]);

  const getConnection = useCallback((provider: Provider): UserConnection | null => {
    return connections.find((c) => c.provider === provider) || null;
  }, [connections]);

  const getActivityCount = useCallback(async (provider: Provider): Promise<number> => {
    switch (provider) {
      case "strava":
        return stravaActivityCount;
      case "google_calendar":
      case "gmail":
      case "google_drive":
      case "outlook":
      case "outlook_mail":
      case "onedrive":
      case "apple_calendar":
        // V1.1 doesn't ship a per-user agenda count yet — ConnectionCard
        // shows the wearable-style "zeroActivities" hint until V2 exposes
        // a calendar-event tally.
        return 0;
      case "google_tasks":
        return googleTasksCount;
      case "apple_health":
      case "health_connect":
        // Soma das três tabelas que o hub do SO alimenta (noites + treinos +
        // métricas). Ver o bloco de contadores no refresh().
        return healthItemCount;
      case "notion":
        return notionItemCount;
      case "spotify":
        return spotifyItemCount;
    }
  }, [stravaActivityCount, googleTasksCount, healthItemCount, notionItemCount, spotifyItemCount]);

  return {
    connections,
    // Distinto de `connections.length === 0`: não-nulo significa "não consegui
    // LER", e a tela precisa dizer isso em vez de oferecer "Conectar" para
    // quem já está conectado.
    connectionsError,
    connectionOwner: owner.current,
    stravaConnection,
    stravaActivityCount,
    googleCalConnection,
    loading,
    syncing,
    // O granular é o que os cards usam; o boolean derivado fica para quem só
    // quer saber "tem UM fluxo de conexão em andamento?".
    connectingProvider,
    connecting: connectingProvider !== null,
    refresh,
    connectStrava,
    syncStrava,
    disconnectStrava,
    connectGoogleCal,
    syncGoogleCal,
    disconnectGoogleCal,
    gmailConnection,
    connectGmail,
    syncGmail,
    disconnectGmail,
    googleDriveConnection,
    connectGoogleDrive,
    syncGoogleDrive,
    disconnectGoogleDrive,
    appleCalConnection,
    connectAppleCal,
    syncAppleCal,
    disconnectAppleCal,
    outlookConnection,
    connectOutlook,
    syncOutlook,
    disconnectOutlook,
    outlookMailConnection,
    connectOutlookMail,
    syncOutlookMail,
    disconnectOutlookMail,
    oneDriveConnection,
    connectOneDrive,
    syncOneDrive,
    disconnectOneDrive,
    googleTasksConnection,
    googleTasksCount,
    connectGoogleTasks,
    syncGoogleTasks,
    disconnectGoogleTasks,
    healthConnection,
    healthItemCount,
    connectHealth,
    syncHealth,
    disconnectHealth,
    fixHealthPermissions,
    notionConnection,
    notionItemCount,
    connectNotion,
    syncNotion,
    disconnectNotion,
    spotifyConnection,
    spotifyItemCount,
    connectSpotify,
    syncSpotify,
    disconnectSpotify,
    // Multi-provider facade (W3 prep)
    connect,
    sync,
    disconnect,
    getConnection,
    getActivityCount,
  };
}
